import React from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useUiLang, pickSurahName, t } from '../utils/lang.js';
import { qfetch } from '../utils/http.js';

export default function SearchBar({ isOpen, onClose }){
  const navigate = useNavigate();
  const uiLang = useUiLang();
  const inputRef = React.useRef(null);
  const wrapRef = React.useRef(null);
  const [q, setQ] = React.useState('');
  const [surahIndex, setSurahIndex] = React.useState([]);

  React.useEffect(()=>{
    if (!isOpen) return;
    let ok = true;
    qfetch('index/surah_index.json')
      .then(r=>r.json())
      .then(j=>{ if(!ok) return; setSurahIndex(j||[]); })
      .catch(()=>{});
    const t = setTimeout(()=>{ try{ inputRef.current?.focus(); }catch{} }, 50);
    return ()=>{ ok=false; clearTimeout(t); };
  }, [isOpen]);

  React.useEffect(()=>{
    if (!isOpen) return;
    const onDoc = (e)=>{ if (!wrapRef.current) return; if (!wrapRef.current.contains(e.target)) onClose?.(); };
    const onEsc = (e)=>{ if (e.key==='Escape') onClose?.(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return ()=>{ document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const go = (path)=>{ onClose?.(); navigate(path); };

  const val = (q||'').trim();
  const suggestions = [];

  const refMatch = val.match(/^(\d{1,3})(?::(\d{1,3}))?$/);
  if (refMatch){
    const s = Math.min(114, Math.max(1, parseInt(refMatch[1],10)));
    const a = refMatch[2] ? Math.max(1, parseInt(refMatch[2],10)) : null;
    suggestions.push({ key:`ref-${s}-${a||''}`, label: a?t('go_to_ref',{ref:`${s}:${a}`}):t('open_surah',{n:s}), action: ()=> go(a?`/surah/${s}#${a}`:`/surah/${s}`) });
  }

  const surahMatches = (surahIndex||[]).filter(item => {
    if (!val) return false;
    const title = (pickSurahName(item.meta, uiLang)||'').toLowerCase();
    const extra = `${item.meta?.englishName||''} ${item.meta?.name||''} ${item.meta?.russianName||''}`.toLowerCase();
    return title.includes(val.toLowerCase()) || extra.includes(val.toLowerCase());
  }).slice(0, 5);
  for (const s of surahMatches){
    suggestions.push({ key:`s-${s.id}`, label:`${pickSurahName(s.meta, uiLang)} · ${s.meta?.numberOfAyahs} ${t('ayahs')}`, action: ()=> go(`/surah/${s.id}`) });
  }

  const onlyDigits = val.replace(/\D+/g,'');
  if (onlyDigits){
    const j = parseInt(onlyDigits,10);
    if (j>=1 && j<=30) suggestions.push({ key:`j-${j}`, label:`${t('juz')} ${j}`, action: ()=> go(`/juz/${j}`) });
    if (j>=1 && j<=60) suggestions.push({ key:`h-${j}`, label:`${t('hizb')} ${j}`, action: ()=> go(`/hizb/${j}`) });
    if (j>=1 && j<=604) suggestions.push({ key:`p-${j}`, label:`${t('page')} ${j}`, action: ()=> go(`/page/${j}`) });
  }

  const onEnter = ()=>{
    if (suggestions.length>0){ suggestions[0].action(); return; }
    const n = parseInt(val,10);
    if (Number.isFinite(n) && n>=1 && n<=114) go(`/surah/${n}`);
  };

  const slot = document.getElementById('subheader-slot');
  if (!slot) return null;

  return ReactDOM.createPortal(
    <div ref={wrapRef} style={{padding:'8px 8px 0'}}>
      <div style={{background:'var(--bg)', border:'1px solid var(--stroke)', borderRadius:10, boxShadow:'0 8px 24px rgba(0,0,0,0.25)'}}>
        <div style={{padding:'8px'}}>
          <input
            ref={inputRef}
            value={q}
            onChange={(e)=>setQ(e.target.value)}
            onKeyDown={(e)=>{ if(e.key==='Enter') onEnter(); }}
            placeholder={t('search_placeholder')}
            style={{width:'100%', padding:'10px 12px', border:'1px solid var(--stroke)', borderRadius:8, background:'var(--bg)', color:'var(--text)'}}
          />
        </div>
        {suggestions.length>0 && (
          <div style={{borderTop:'1px solid var(--stroke)'}}>
            {suggestions.slice(0,8).map(sug => (
              <button key={sug.key} onClick={sug.action} className="audio-player__dropdown-item" style={{display:'block', width:'100%', textAlign:'left'}}>
                {sug.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>,
    slot
  );
}
